import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Button,
	Checkbox,
	Input,
	useToast,
	VStack,
	Text,
	FormControl,
	FormLabel,
	InputGroup,
	InputRightElement,
	Select,
	Box,
	Code,
	Collapse,
	useDisclosure,
	Flex,
	IconButton,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import globalStore from '../store';
import { CalendarIcon, ChevronDownIcon, ChevronRightIcon, CopyIcon } from '@chakra-ui/icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Countries } from './enums';
import { Platform } from '@repo/alictus-common/enums/platform';
import config from '../config';

const ENDPOINT = `${config.API_ENDPOINT}/game/com.ck.tripletiles/rc/fetch_by_playfabid`;
const USERS_ENDPOINT = `${config.API_ENDPOINT}/game/com.ck.tripletiles/segment/users-devices`;
const OVERRIDE_AUTH_TOKEN = '5hmSZzNtiZTOcKdRtLSPPqHxWbiKZx35';

// Prepare country list for dropdown
const countryList = Countries.filter((country) => country.code !== 'All').map((country) => ({
	value: country.code,
	label: country.name,
}));

type Device = {
	id: string;
	label: string;
};

type User = {
	id: string;
	name: string;
	devices: Device[];
};

type RemoteConfigResult = {
	key: string;
	value: any;
	type: string;
	description?: string;
};

type ABTestResult = {
	abTestName: string;
	variantName: string;
	keys: Array<{
		key: string;
		type: string;
	}>;
};

// Component for individual remote config item
function RemoteConfigItem({ rc, index }: { rc: RemoteConfigResult; index: number }) {
	const { isOpen, onToggle } = useDisclosure();
	const toast = useToast();

	const formatValue = (value: any, type: string) => {
		if (type === 'String' || type === 'Virtual') {
			try {
				const parsed = JSON.parse(value);
				return JSON.stringify(parsed, null, 2);
			} catch {
				return value;
			}
		}
		return value;
	};

	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
			toast({
				title: 'Copied to clipboard',
				status: 'success',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		} catch (error) {
			toast({
				title: 'Failed to copy',
				status: 'error',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		}
	};

	const fullValue = formatValue(rc.value, rc.type);

	return (
		<Box key={index} border="1px" borderColor="gray.500" borderRadius="md" width="100%">
			<Flex align="center" justify="space-between" p={3} cursor="pointer" onClick={onToggle}>
				<Flex align="center" gap={2} flex={1}>
					<Box>{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</Box>
					<VStack align="start" spacing={1} flex={1}>
						<Flex align="center" gap={2}>
							<Text fontWeight="semibold">
								{rc.key}{' '}
								<Code fontSize="xs" colorScheme="blue">
									{rc.type}
								</Code>
							</Text>
						</Flex>
						{rc.description && (
							<Text fontSize="sm" color="gray.300" fontStyle="italic">
								{rc.description}
							</Text>
						)}
					</VStack>
				</Flex>

				<IconButton
					aria-label="Copy value"
					icon={<CopyIcon />}
					size="sm"
					variant="ghost"
					onClick={(e) => {
						e.stopPropagation();
						copyToClipboard(fullValue);
					}}
				/>
			</Flex>

			<Collapse in={isOpen}>
				<Box px={3} pb={3}>
					<Code p={2} fontSize="sm" width="100%" whiteSpace="pre-wrap" display="block">
						{fullValue}
					</Code>
				</Box>
			</Collapse>
		</Box>
	);
}

// Component for individual AB test item
function ABTestItem({ abTest, index }: { abTest: ABTestResult; index: number }) {
	return (
		<Box key={index} border="1px" borderColor="gray.500" borderRadius="md" width="100%">
			<Box p={3}>
				<VStack align="start" spacing={2}>
					{/* AB Test Name and Variant - Primary display */}
					<Flex align="center" gap={2} wrap="wrap">
						<Text fontWeight="bold">{abTest.abTestName}</Text>
						<Text fontWeight="semibold">→ {abTest.variantName}</Text>
						<Code fontSize="xs" colorScheme="blue">
							AB Test
						</Code>
					</Flex>

					{/* List all keys with their types */}
					<VStack align="start" spacing={1} pl={4}>
						{abTest.keys.map((keyData, keyIndex) => (
							<Flex key={keyIndex} align="center" gap={2}>
								<Text fontWeight="medium" fontSize="sm">
									{keyData.key}
								</Text>
								<Code fontSize="xs" colorScheme="blue">
									{keyData.type}
								</Code>
							</Flex>
						))}
					</VStack>
				</VStack>
			</Box>
		</Box>
	);
}

export default function FetchRCByPlayFabModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
	const [playFabId, setPlayFabId] = useState('');
	const [loading, setLoading] = useState(false);
	const [remoteConfigs, setRemoteConfigs] = useState<RemoteConfigResult[]>([]);
	const [abTests, setABTests] = useState<ABTestResult[]>([]);
	const [country, setCountry] = useState<string | null>(null);
	const [manualInput, setManualInput] = useState('');
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [version, setVersion] = useState('');
	const [buildNumber, setBuildNumber] = useState('');
	const [platform, setPlatform] = useState(Platform.iOS); // default to iOS
	const [newUser, setNewUser] = useState(false); // default false
	const [selectedUserId, setSelectedUserId] = useState<string>('');
	const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
	const [users, setUsers] = useState<User[]>([]);
	const [loadingUsers, setLoadingUsers] = useState(false);
	const [playerId, setPlayerId] = useState('');
	const [disableTargetedPriority, setDisableTargetedPriority] = useState(false);

	const sortedUsers = users.sort((a, b) => a.name.localeCompare(b.name));
	const selectedUser = users.find((u) => String(u.id) === selectedUserId);

	const fetchUsersAndDevices = async () => {
		setLoadingUsers(true);
		try {
			const headers = {
				'Content-Type': 'application/json',
				Authorization: 'Bearer ' + globalData.JWTToken,
			};

			const response = await axios.get(USERS_ENDPOINT, { headers });
			setUsers(response.data?.users || []);
		} catch (error) {
			console.error('Error fetching users and devices:', error);
			toast({
				title: 'Error fetching users and devices',
				description: 'Failed to load users and devices',
				status: 'error',
				duration: 4000,
				isClosable: true,
				position: 'top',
			});
		} finally {
			setLoadingUsers(false);
		}
	};

	useEffect(() => {
		if (!isOpen) {
			setPlayFabId('');
			setPlayerId('');
			setLoading(false);
			setRemoteConfigs([]);
			setABTests([]);
			setCountry(null);
			setManualInput('');
			setSelectedDate(null);
			setVersion('');
			setBuildNumber('');
			setPlatform(Platform.iOS);
			setNewUser(false);
			setSelectedUserId('');
			setSelectedDeviceId('');
			setUsers([]);
			setLoadingUsers(false);
			setDisableTargetedPriority(false);
		}
		if (isOpen) {
			fetchUsersAndDevices();
		}
	}, [isOpen]);

	const toast = useToast();
	let globalData = globalStore();

	// Handle time input (epoch seconds)
	const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setManualInput(value);
		if (!isNaN(Number(value)) && Number(value) > 0) {
			setSelectedDate(new Date(Number(value) * 1000));
		} else {
			setSelectedDate(null);
		}
	};

	const handleDateChange = (date: Date | null) => {
		setSelectedDate(date);
		if (date) {
			const epochTime = Math.floor(date.getTime() / 1000);
			setManualInput(epochTime.toString());
		} else {
			setManualInput('');
		}
	};

	const handleFetch = async () => {
		setLoading(true);
		setRemoteConfigs([]);
		setABTests([]); // Reset AB Tests
		try {
			// Generate random Player ID if not provided
			const finalPlayerId = playerId.trim() || (Math.random() < 0.5 ? 'abc' : 'PLAYER_ID1');

			const body = {
				PlayerId: finalPlayerId,
				RcIds: [],
				ABTests: [],
				PlayfabId: playFabId,
				BundleId: 'com.ck.tripletiles',
				Platform: platform,
				DeviceId: selectedDeviceId || '',
				Version: version ? version : '',
				BuildNumber: buildNumber ? Number(buildNumber) : null,
				NewUser: newUser,
				BackendTimestamp: 1000,
			};

			const headers: any = {
				'Content-Type': 'application/json',
				Authorization: 'Bearer ' + globalData.JWTToken,
			};

			// Add override headers only if needed
			let overrideUsed = false;
			if (country) {
				headers['X-Country-Override'] = country;
				overrideUsed = true;
			}
			if (manualInput && !isNaN(Number(manualInput)) && Number(manualInput) > 0) {
				headers['X-Time-Override'] = manualInput;
				overrideUsed = true;
			}
			if (disableTargetedPriority) {
				headers['X-Targeted-Test-Override'] = 'false';
				overrideUsed = true;
			}
			if (overrideUsed) {
				headers['X-Override-Auth'] = OVERRIDE_AUTH_TOKEN;
			}

			console.log('Fetching remote configs with body:', body, 'and headers:', headers);
			const res = await axios.post(ENDPOINT, body, { headers });
			console.log('Fetch remote configs response:', res);

			// Separate Remote Configs and AB Tests
			setRemoteConfigs(res.data?.RemoteConfigs || []);

			setABTests(res.data?.ABTestConfigs || []);

			const totalConfigs = (res.data?.RemoteConfigs || []).length + (res.data?.ABTestConfigs || []).length;

			if (totalConfigs === 0) {
				toast({
					title: 'No remote configs or AB tests found',
					status: 'info',
					duration: 4000,
					isClosable: true,
					position: 'top',
				});
			}
		} catch (e: any) {
			console.error('Error fetching remote configs:', e);

			// Safely extract error message from different possible error structures
			let errorMessage = 'Unknown error occurred';
			let errorDetails = '';

			if (e.response) {
				// Server responded with an error status
				if (e.response.data) {
					if (typeof e.response.data === 'string') {
						errorMessage = e.response.data;
					} else if (e.response.data.error) {
						errorMessage = e.response.data.error;
						if (e.response.data.details) {
							errorDetails = e.response.data.details;
						}
					} else {
						errorMessage = JSON.stringify(e.response.data);
					}
				} else {
					errorMessage = `Server error: ${e.response.status} ${e.response.statusText}`;
				}
			} else if (e.request) {
				// Request was made but no response received
				errorMessage = 'No response from server. Please check your connection.';
			} else if (e.message) {
				// Something else happened
				errorMessage = e.message;
			}

			// Show appropriate toast based on error type
			const isValidationError = e.response?.status === 400;

			toast({
				title: isValidationError ? 'Validation Error' : 'Error fetching remote configs',
				description: errorDetails ? `${errorMessage}\n\nDetails: ${errorDetails}` : errorMessage,
				status: isValidationError ? 'warning' : 'error',
				duration: isValidationError ? 8000 : 6000,
				isClosable: true,
				position: 'top',
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl">
			<ModalOverlay />
			<ModalContent maxW="800px">
				<ModalHeader>Fetch AB Tests and Remote Configs by PlayFabId</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<FormControl mb={4} isRequired>
						<FormLabel>PlayFabId</FormLabel>
						<Input placeholder="Enter PlayFabId" value={playFabId} onChange={(e) => setPlayFabId(e.target.value)} />
					</FormControl>
					<FormControl mb={4} isRequired>
						<FormLabel>Platform</FormLabel>
						<Select value={platform} onChange={(e) => setPlatform(Number(e.target.value))}>
							<option value={Platform.iOS}>iOS</option>
							<option value={Platform.Android}>Android</option>
						</Select>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Player ID (optional)</FormLabel>
						<Input placeholder="Enter Player ID (leave empty for random)" value={playerId} onChange={(e) => setPlayerId(e.target.value)} />
						<Checkbox mt={2} isChecked={disableTargetedPriority} onChange={(e) => setDisableTargetedPriority(e.target.checked)}>
							Disable Targeted Test Priority
						</Checkbox>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Version (optional)</FormLabel>
						<Input placeholder="e.g. 1.20.0" value={version} onChange={(e) => setVersion(e.target.value)} />
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Build Number (optional)</FormLabel>
						<Input placeholder="e.g. 3094" type="number" value={buildNumber} onChange={(e) => setBuildNumber(e.target.value)} />
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Country (optional)</FormLabel>
						<Select placeholder="Select Country" value={country || ''} onChange={(e) => setCountry(e.target.value || null)}>
							<option value="">(none)</option>
							{countryList.map((c) => (
								<option key={c.value} value={c.value}>
									{c.label}
								</option>
							))}
						</Select>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Time (Epoch, optional)</FormLabel>
						<InputGroup>
							<Input type="number" step="1" value={manualInput} onChange={handleManualInputChange} placeholder="Enter epoch time" />
							<InputRightElement opacity={0.5} pointerEvents="none" minW={180}>
								{manualInput && !isNaN(Number(manualInput)) && Number(manualInput) > 0
									? new Date(Number(manualInput) * 1000).toLocaleString('tr-TR')
									: ''}
							</InputRightElement>
						</InputGroup>
						<DatePicker
							selected={selectedDate}
							onChange={handleDateChange}
							showTimeSelect
							timeIntervals={1}
							customInput={
								<Button size="sm" mt="1" colorScheme="blue" leftIcon={<CalendarIcon />}>
									Pick
								</Button>
							}
						/>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>User (optional)</FormLabel>
						<Select
							placeholder={loadingUsers ? 'Loading users...' : 'Select User'}
							value={selectedUserId}
							onChange={(e) => {
								setSelectedUserId(e.target.value);
								setSelectedDeviceId(''); // Reset device when user changes
							}}
							isDisabled={loadingUsers}
						>
							{sortedUsers.map((user) => (
								<option key={user.id} value={user.id}>
									{user.name}
								</option>
							))}
						</Select>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Device ID (optional)</FormLabel>
						<Select
							placeholder="Select Device"
							value={selectedDeviceId}
							onChange={(e) => setSelectedDeviceId(e.target.value)}
							isDisabled={!selectedUser || loadingUsers}
						>
							{selectedUser?.devices.map((device) => (
								<option key={device.id} value={device.id}>
									{device.label} ({device.id})
								</option>
							))}
						</Select>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>New User (optional)</FormLabel>
						<Select value={String(newUser)} onChange={(e) => setNewUser(e.target.value === 'true')}>
							<option value="false">No</option>
							<option value="true">Yes</option>
						</Select>
					</FormControl>
					<Button colorScheme="blue" onClick={handleFetch} isLoading={loading} isDisabled={!playFabId} mb={3} width="100%">
						Fetch
					</Button>
					{abTests.length > 0 && (
						<Box mb={6}>
							<Text fontWeight="bold" mb={3}>
								AB Tests ({abTests.length})
							</Text>
							<VStack spacing={4} align="stretch">
								{abTests.map((abTest, index) => (
									<ABTestItem key={`abtest-${index}`} abTest={abTest} index={index} />
								))}
							</VStack>
						</Box>
					)}
					<VStack align="start" spacing={3} maxHeight="400px" overflowY="auto">
						{remoteConfigs.length > 0 && <Text fontWeight="bold">Remote Configs ({remoteConfigs.length}):</Text>}
						{remoteConfigs.map((rc, idx) => (
							<RemoteConfigItem key={idx} rc={rc} index={idx} />
						))}
					</VStack>
				</ModalBody>
				<ModalFooter>
					<Button onClick={onClose}>Close</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
